<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { mapGetters } from 'vuex';
import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
  },
  mixins: [globalConfigMixin],
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
    };
  },

  validations: {
    pageName: {
      required,
    },

    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        this.runFBInit(); // run init anyway, `tryFBlogin` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(); // make an attempt to login
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          // if the error was related to script loading, we show a toast
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'), {type: 'info'});
        } else {
          // if the error was anything else, we capture it and show a toast
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'), {type: 'error'});
        }
      }
    },

    setPageName({ name }) {
      this.v$.selectedPage.$touch();
      this.pageName = name;
    },

    initChannelAuth(channel) {
      if (channel === 'facebook') {
        this.loadFBsdk();
      }
    },

    runFBInit() {
      FB.init({
        appId: window.hichatConfig.fbAppId,
        xfbml: true,
        version: window.hichatConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope: 'pages_manage_posts,pages_manage_engagement,email,pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages,pages_read_user_content,public_profile,ads_management,ads_read,page_events,pages_manage_ads,pages_utility_messaging'
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // Ignore error
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name,
      };
    },


    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(() => {
            this.isCreating = false;
          });
      }
    },
  },

};
</script>
<template>
  <div class="w-full h-full">
    <!-- PageHeader cho trạng thái ban đầu -->
    <div v-if="!hasLoginStarted" class="w-full">
      <PageHeader :header-title="$t('INBOX_MGMT.ADD.FB.TITLE')" :header-content="useInstallationName(
        $t('INBOX_MGMT.ADD.FB.HELP'),
        globalConfig.installationName
      )
        " />
      <div class="flex flex-col items-center justify-center h-full text-center mt-12">
        <a href="#" @click="startLogin()">
          <img class="w-auto h-10" src="~dashboard/assets/images/channels/facebook_login.png" alt="Facebook-logo" />
        </a>
      </div>
    </div>

    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p v-if="errorStateDescription" v-dompurify-html="errorStateDescription" />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form v-else class="flex flex-wrap mx-0 gap-6" @submit.prevent="createChannel()">
        <div class="w-full">
          <PageHeader :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')" :header-content="useInstallationName(
            $t('INBOX_MGMT.ADD.DETAILS.DESC'),
            globalConfig.installationName
          )
            " />
        </div>
        <div class="w-full">
          <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
            {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
            <multiselect v-model="selectedPage" close-on-select allow-empty :options="getSelectablePages" track-by="id"
              label="name" :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')" selected-label @select="setPageName" />
            <span v-if="v$.selectedPage.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
            </span>
          </div>
        </div>
        <div class="w-full">
          <label :class="{ error: v$.pageName.$error }">
            {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
            <input v-model="pageName" type="text" :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
              @input="v$.pageName.$touch" />
            <span v-if="v$.pageName.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
            </span>
          </label>
        </div>
        <div class="w-full flex justify-center">
          <button type="submit" class="button !bg-n-bluegray-4 !text-white rounded-lg">Create Inbox</button>
        </div>
      </form>
    </div>
  </div>
</template>